
export const giveawayRequiredMinutes = 60;
export const giveawayWindowDays = 7;
export const giveawayWindowMs = giveawayWindowDays * 86_400_000;

export interface WebsiteGiveaway {
  id: string;
  serverId: string;
  title: string;
  description: string;
  prize: string;
  status: "open" | "drawn" | "cancelled";
  opensAt: Date;
  closesAt: Date;
  createdByUserId: string;
  seedSecret: string;
  seedCommitment: string;
  drawnAt: Date | null;
  winnerEntryId: string | null;
  winnerName: string | null;
  drawDigest: string | null;
  cancelledAt: Date | null;
  cancellationReason: string | null;
  archivedAt?: Date | null;
}

export interface GiveawayEntry {
  id: string;
  giveawayId: string;
  userId: string;
  steam64Id: string;
  displayName: string;
  enteredAt: Date;
  qualifyingMinutes: number;
  windowStartedAt: Date;
}

export interface GiveawayEligibility {
  steamLinked: boolean;
  playedMinutes: number;
  requiredMinutes: number;
  windowDays: number;
  eligible: boolean;
  checkedAt: Date;
}

export interface GiveawayView {
  giveaway: WebsiteGiveaway;
  entryCount: number;
  entry: GiveawayEntry | null;
  eligibility: GiveawayEligibility;
}

export interface PublishGiveaway {
  id: string;
  serverId: string;
  title: string;
  description: string;
  prize: string;
  closesAt: Date;
  actorUserId: string;
}

export interface GiveawayRepository {
  current(serverId: string, userId: string): Promise<GiveawayView | null>;
  publish(input: PublishGiveaway): Promise<WebsiteGiveaway>;
  enter(serverId: string, giveawayId: string, userId: string): Promise<{ entry: GiveawayEntry; alreadyEntered: boolean }>;
  cancel(serverId: string, giveawayId: string, actorUserId: string, reason: string): Promise<void>;
  archive(serverId: string, giveawayId: string, actorUserId: string): Promise<void>;
  drawDue(serverId: string): Promise<number>;
  prunePresence(before: Date): Promise<void>;
}

export class GiveawayError extends Error {
  constructor(message: string, readonly statusCode = 409) { super(message); }
}

export function giveawayEligibility(steamLinked: boolean, playedMinutes: number, checkedAt: Date): GiveawayEligibility {
  const minutes = Math.max(0, Math.floor(playedMinutes));
  return { steamLinked, playedMinutes: minutes, requiredMinutes: giveawayRequiredMinutes, windowDays: giveawayWindowDays,
    eligible: steamLinked && minutes >= giveawayRequiredMinutes, checkedAt };
}

export function validateGiveaway(input: PublishGiveaway, now: Date): void {
  if (!Number.isFinite(input.closesAt.getTime()) || input.closesAt <= now || input.closesAt.getTime() > now.getTime() + 90 * 86_400_000) {
    throw new GiveawayError("Choose a future closing time within 90 days.", 422);
  }
}

export function giveawaySeedCommitment(seed: string, hash: (value: string) => string): string {
  return hash(seed);
}

/** Independent uniformly distributed 256-bit ticket scores; smallest score wins.
 * The seed is committed before entry, stored durably, and revealed only on draw.
 * Sorting makes retries and database row order irrelevant. No reroll operation exists.
 */
export function drawGiveaway(seed: string, entries: GiveawayEntry[], hash: (value: string) => string): { winner: GiveawayEntry | null; digest: string } {
  const ordered = [...entries].sort((a, b) => a.id.localeCompare(b.id));
  const digest = hash(ordered.map((entry) => entry.id).join("\n"));
  const scores = ordered.map((entry) => ({ entry, score: hash(`${seed}\n${entry.id}`) }));
  scores.sort((a, b) => a.score.localeCompare(b.score) || a.entry.id.localeCompare(b.entry.id));
  return { winner: scores[0]?.entry ?? null, digest };
}

/** Daily history cannot tell which side of a seven-day boundary old minutes fell on.
 * Use its mathematically guaranteed lower bound, or exact minute evidence, whichever
 * is larger. Never prorate, double-count, or include an entire partly expired day.
 */
export function rollingGiveawayMinutes(
  days: Array<{ day: Date; playtimeMinutes: number; lastCountedMinute?: Date | null }>,
  exactMinutes: Date[], now: Date
): number {
  const from = now.getTime() - giveawayWindowMs;
  const exactByDay = new Map<number, Set<number>>();
  for (const minute of exactMinutes) {
    const timestamp = minute.getTime();
    if (timestamp < from || timestamp > now.getTime()) continue;
    const day = Math.floor(timestamp / 86_400_000) * 86_400_000;
    const set = exactByDay.get(day) ?? new Set<number>();
    set.add(Math.floor(timestamp / 60_000));
    exactByDay.set(day, set);
  }
  const totals = new Map([...exactByDay].map(([day, minutes]) => [day, minutes.size]));
  for (const row of days) {
    const start = row.day.getTime();
    if (start > now.getTime() || start + 86_400_000 <= from) continue;
    const before = Math.max(0, Math.min(1440, Math.ceil((from - start) / 60_000)));
    const lastPossibleMinute = row.lastCountedMinute ? Math.min(1439, Math.floor((row.lastCountedMinute.getTime() - start) / 60_000)) : 1439;
    const after = Math.max(0, Math.min(1440, lastPossibleMinute - Math.floor((now.getTime() - start) / 60_000)));
    const guaranteed = Math.max(0, Math.min(1440, row.playtimeMinutes) - before - after);
    totals.set(start, Math.max(totals.get(start) ?? 0, guaranteed));
  }
  return [...totals.values()].reduce((total, minutes) => total + minutes, 0);
}
